import { resolveAgendaTimestampLabel } from '@govinity/shared-config/meeting-event-timestamps';
import { MeetingEventTimestampDataType } from '@govinity/validation';
import type {
  MeetingCurrentLiveItemResponse,
  MeetingEventPublicAgendaSection,
  MeetingEventTimestampPublicResponse,
} from '@govinity/validation/public';

/**
 * Builds lookup maps from agenda section/item IDs to their display titles, plus parallel maps to
 * their outline numbers ("4", "4a") when configured. The number maps let media-index rows prefix
 * their title the same way the agenda tab does; entries without outline numbering are simply absent.
 * Traverses the section tree recursively, including child sections.
 */
export function buildAgendaLookup(sections: MeetingEventPublicAgendaSection[]) {
  const sectionMap = new Map<number, string>();
  const itemMap = new Map<number, string>();
  const sectionNumberMap = new Map<number, string>();
  const itemNumberMap = new Map<number, string>();
  // Depth-first order index per section id / item placement id. Lets the media-index timeline render
  // in agenda-tree order (each item nested under its section) rather than raw timestamp order, so an
  // item whose timestamp precedes its section header doesn't float up under the previous section.
  const sectionOrderById = new Map<number, number>();
  const itemOrderById = new Map<number, number>();
  let order = 0;

  function traverseItems(section: MeetingEventPublicAgendaSection) {
    for (const item of section.legislativeItems) {
      // Keyed by the placement (AgendaSectionLegislativeItem) id — the agenda_section_item timestamp
      // dataId resolves against this (GOV-1582).
      itemMap.set(
        item.agendaSectionItemId,
        item.shortTitle ?? item.fullTitle ?? item.code ?? `Item ${item.id}`
      );
      itemOrderById.set(item.agendaSectionItemId, order++);
      if (item.itemNumber) {
        itemNumberMap.set(item.agendaSectionItemId, item.itemNumber);
      }
    }
  }

  function traverse(section: MeetingEventPublicAgendaSection) {
    sectionMap.set(section.id, section.title);
    sectionOrderById.set(section.id, order++);
    if (section.sectionNumber) {
      sectionNumberMap.set(section.id, section.sectionNumber);
    }
    if (!section.displayChildSectionsFirst) {
      traverseItems(section);
    }
    for (const child of section.childSections ?? []) {
      traverse(child);
    }
    if (section.displayChildSectionsFirst) {
      traverseItems(section);
    }
  }

  for (const section of sections) {
    traverse(section);
  }

  return { sectionMap, itemMap, sectionNumberMap, itemNumberMap, sectionOrderById, itemOrderById };
}

/** The section/item title and number lookups consumed by the timestamp label resolvers. */
export type AgendaLookup = {
  sectionMap: Map<number, string>;
  itemMap: Map<number, string>;
  sectionNumberMap: Map<number, string>;
  itemNumberMap: Map<number, string>;
};

/** Agenda-tree order indices keyed by section id / item placement id, from {@link buildAgendaLookup}. */
export type AgendaOrderLookup = {
  sectionOrderById: Map<number, number>;
  itemOrderById: Map<number, number>;
};

/**
 * Prefixes a title with its agenda outline number ("4a" → "4a test"), matching the agenda tab and
 * internal media view. The stored number already carries the government's configured outline
 * formatting (e.g. "4a", "4a)", "(4a)"), so it is rendered verbatim rather than having a separator
 * appended; a missing number yields the bare title.
 */
export function withOutlineNumber(number: string | null | undefined, title: string) {
  return number ? `${number} ${title}` : title;
}

/**
 * Resolves the numbered display label for a timestamp, shared by the media-index timeline and the
 * "Now:" indicator so they never diverge. Handles agenda section/item timestamps (resolved through
 * the lookup maps), entry-level minutes timestamps (labelled by their first event's target plus the
 * entry's outline number), and label-only document timestamps. Returns `null` when no meaningful
 * label can be derived — e.g. minute-record timestamps (roll call, note, motion) that nest inside
 * another row, or an entry-level timestamp whose events carry no target.
 */
export function resolveTimestampLabel(
  ts: MeetingEventTimestampPublicResponse,
  { sectionMap, itemMap, sectionNumberMap, itemNumberMap }: AgendaLookup
): string | null {
  const { dataId, dataType } = ts;

  if (
    dataType === MeetingEventTimestampDataType.AgendaSection ||
    dataType === MeetingEventTimestampDataType.AgendaSectionItem
  ) {
    // Shared with the search-index builder so both resolve section/item titles identically.
    const title = resolveAgendaTimestampLabel(
      { dataType, dataId, label: ts.label },
      { sectionTitleById: sectionMap, itemTitleById: itemMap }
    );
    if (!title) return null;

    const numberMap =
      dataType === MeetingEventTimestampDataType.AgendaSection ? sectionNumberMap : itemNumberMap;
    return withOutlineNumber(dataId ? numberMap.get(dataId) : null, title);
  }

  // dataType === null covers both uploaded-agenda label-only timestamps and entry-level minutes
  // timestamps that carry events but no placement id/label.
  if (ts.dataType === null) {
    if (ts.label) return ts.label;

    const firstEvent = ts.events?.[0];
    if (!firstEvent) return null;

    const eventLabel =
      firstEvent.type === 'privileged_motion'
        ? firstEvent.actionName
        : 'target' in firstEvent
          ? firstEvent.target
          : undefined;
    return eventLabel ? withOutlineNumber(ts.entryNumber, eventLabel) : null;
  }

  return null;
}

/**
 * Resolves the numbered "Now:" label for a live meeting's floored entry against the same agenda
 * lookup maps the recorded indicator uses, so live and recorded read identically. The entry is
 * identified only by its section/placement id (the endpoint sends no titles); resolving against the
 * already-security-scrubbed public agenda means an entry absent from it yields `null` → "No active
 * item", never a leaked title.
 */
export function resolveCurrentLiveItemLabel(
  item: MeetingCurrentLiveItemResponse,
  { sectionMap, itemMap, sectionNumberMap, itemNumberMap }: AgendaLookup
): string | null {
  if (!item) return null;

  if (item.kind === 'item') {
    const title = itemMap.get(item.agendaSectionItemId);
    return title ? withOutlineNumber(itemNumberMap.get(item.agendaSectionItemId), title) : null;
  }

  const title = sectionMap.get(item.agendaSectionId);
  return title ? withOutlineNumber(sectionNumberMap.get(item.agendaSectionId), title) : null;
}
